/**
 * No-follow filesystem containment primitives shared by every hub-as-code read/write of
 * workspace files (resources + eval/journey attachments), gh #3365.
 *
 * Lexical containment (`path.resolve` + `startsWith`) is NOT enough: `statSync`,
 * `readFileSync`, `writeFileSync`, and `readdir`/`rm` all FOLLOW symlinks. A workspace can
 * be attacker-authored (a CI/GitOps checkout of a feature branch), so a symlinked file or
 * directory can otherwise disclose an external file on push, or overwrite one on pull.
 * These helpers resolve real paths and refuse to follow a symlinked destination.
 *
 * SCOPE — these close the realistic threat: a STATIC symlink planted in the checked-out
 * workspace. A residual TOCTOU remains inherent to path-based fs: a concurrent local process
 * could swap a verified directory component for a symlink between a check and the next
 * operation. That is deliberately out of scope — it requires an attacker who already has
 * concurrent write access to the victim's own workspace (and could tamper with it directly),
 * and a race-free fix needs `openat`/`O_NOFOLLOW` directory-handle-relative syscalls that
 * Node's portable synchronous `fs` does not expose. `wayai` runs on the user's own machine /
 * a fresh CI checkout, where that threat model does not apply.
 */

import * as fs from 'node:fs';
import * as path from 'node:path';
import { expected } from './expected.js';

/**
 * The set of working-tree paths a materialize touched, so every consumer can NAME the files
 * it wrote (gh #3410). Paths are hub-relative. `changed` = files written/overwritten this run
 * (byte-identical writes are skipped and NOT listed); `removed` = files/dirs orphan-pruned.
 */
export interface FileChangeLog {
  changed: string[];
  removed: string[];
}

/**
 * True when `abs` is a REAL regular file (never a symlink) whose bytes equal `data`.
 * lstat-guarded: the comparison read never follows a symlink — a symlinked destination
 * reports "not identical" so the caller's (no-follow) write replaces the link with a real
 * file, preserving the surrounding no-follow discipline. The primitive behind idempotent
 * writes: identical content → skip the write → no mtime churn (gh #3410).
 */
export function fileHasBytes(abs: string, data: Buffer): boolean {
  let st: fs.Stats | undefined;
  try {
    st = fs.lstatSync(abs);
  } catch {
    return false;
  }
  if (!st.isFile() || st.isSymbolicLink()) return false;
  try {
    return fs.readFileSync(abs).equals(data);
  } catch {
    return false;
  }
}

/**
 * True when `abs`'s REAL path (every symlink resolved) is contained within `root`'s real
 * path. Both paths must exist. Use before READING a workspace file we will trust/upload.
 */
export function realpathContained(root: string, abs: string): boolean {
  let realRoot: string;
  let realAbs: string;
  try {
    realRoot = fs.realpathSync(root);
    realAbs = fs.realpathSync(abs);
  } catch {
    return false;
  }
  return realAbs === realRoot || realAbs.startsWith(realRoot + path.sep);
}

/**
 * `mkdir` one non-recursive level, tolerating a concurrent create: on EEXIST (another process
 * made it between our lstat and here), accept ONLY if it is now a real directory — a raced
 * symlink or file is still rejected. Returns false when the level can't be a real directory.
 */
export function mkdirTolerateRace(dir: string): boolean {
  try {
    fs.mkdirSync(dir);
    return true;
  } catch (err) {
    if ((err as NodeJS.ErrnoException).code !== 'EEXIST') throw err;
    let st: fs.Stats | undefined;
    try {
      st = fs.lstatSync(dir);
    } catch {
      st = undefined;
    }
    return !!st?.isDirectory();
  }
}

/**
 * Verify `target` is reachable from the TRUSTED `root` through NO symlinked path component —
 * neither the target itself, an ancestor, nor an inward-pointing symlink — creating any
 * missing segments as real directories when `create` is true.
 *
 * This is stronger than a post-hoc `realpathContained` check, which is defeated when a
 * symlinked ancestor doesn't exist yet: `mkdir -p` would create the target THROUGH the
 * symlink and then the target resolves as its own (attacker-chosen) boundary. Walking each
 * segment with `lstat` (which never follows the final symlink) closes that hole.
 *
 * Returns false — writing/pruning nothing — if `target` is lexically outside `root`, `root`
 * is not a real directory, or any existing segment between them is a symlink or a file.
 */
export function ensureRealSubdirNoSymlink(root: string, target: string, create: boolean): boolean {
  const rootResolved = path.resolve(root);
  const targetResolved = path.resolve(target);
  if (targetResolved !== rootResolved && !targetResolved.startsWith(rootResolved + path.sep)) return false;
  try {
    if (!fs.lstatSync(rootResolved).isDirectory()) return false; // root must be a real dir
  } catch {
    return false;
  }
  const rel = path.relative(rootResolved, targetResolved);
  if (rel === '') return true;
  let cur = rootResolved;
  for (const segment of rel.split(path.sep)) {
    cur = path.join(cur, segment);
    let st: fs.Stats | undefined;
    try {
      st = fs.lstatSync(cur);
    } catch {
      st = undefined;
    }
    if (st) {
      // A symlink (even inward-pointing) reports isDirectory() === false under lstat — reject.
      if (!st.isDirectory()) return false;
    } else if (create) {
      if (!mkdirTolerateRace(cur)) return false; // non-recursive: every parent is verified real
    } else {
      return true; // nothing further exists → nothing to prune, safe no-op
    }
  }
  return true;
}

/** True when `child` is `parent` or sits beneath it. Lexical — the no-follow checks are below. */
export function isUnder(parent: string, child: string): boolean {
  const rel = path.relative(parent, child);
  // `..` as a whole first segment, not a prefix: `..notes.md` is a legitimate name.
  return rel === '' || (rel !== '..' && !rel.startsWith(`..${path.sep}`) && !path.isAbsolute(rel));
}

/** True when `p` itself is a symlink (absent → false). */
export function isSymlink(p: string): boolean {
  try {
    return fs.lstatSync(p).isSymbolicLink();
  } catch {
    return false;
  }
}

/**
 * A refusal to read or write outside the workspace: `expected()`-tagged (the user's to fix,
 * not a CLI bug for Sentry) and marked, so a caller that otherwise treats a failed read as
 * "start over" — pull's overwrite fallback — can tell a refusal apart and stop.
 */
export function workspaceRefusal(message: string): Error {
  const err = expected(message);
  (err as Error & { isWorkspaceRefusal: boolean }).isWorkspaceRefusal = true;
  return err;
}

/** True for an error built by `workspaceRefusal` (or `symlinkRefusal`). */
export function isWorkspaceRefusal(err: unknown): boolean {
  return (err as { isWorkspaceRefusal?: unknown } | null)?.isWorkspaceRefusal === true;
}

/**
 * The refusal every "never through a symlink" check throws. It fires for a symlink at `rel`,
 * one on the way to it, or something of the wrong kind in its place, so it names all three.
 */
export function symlinkRefusal(rel: string, kind: 'file' | 'directory' | 'file or folder'): Error {
  return workspaceRefusal(
    `${rel} is a symlink, is reached through one, or is not a ${kind}. ` +
      `The CLI never reads or writes workspace config through a symlink — make it a real ${kind}.`,
  );
}

/**
 * `ensureRealSubdirNoSymlink`, throwing instead of returning false: for a dir a whole command
 * is bounded by, where refusing one write and carrying on would only relocate the damage.
 * The error is `expected()`-tagged — a planted or hand-made symlink is the user's to fix,
 * not a CLI bug for Sentry.
 */
export function requireRealSubdirNoSymlink(root: string, target: string, create: boolean): void {
  if (ensureRealSubdirNoSymlink(root, target, create)) return;
  throw symlinkRefusal(path.relative(path.dirname(root), target), 'directory');
}

/**
 * The hub-folder subdirs the CLI reads, writes and orphan-prunes. `attachments` restates
 * eval-attachments' `ATTACHMENTS_DIR` — importing it would stop this module being a leaf —
 * and fs-safety.test.ts pins the two equal.
 */
export const HUB_MANAGED_SUBDIRS = ['agents', 'evals', 'journeys', 'resources', 'attachments'] as const;

/** A hub's config file: `hub.yaml`, or the legacy `wayai.yaml` (`resolveHubYamlPath`). */
const HUB_CONFIG_FILES = ['hub.yaml', 'wayai.yaml'] as const;

/**
 * Throw (`expected`) unless the hub folder is a real directory under its parent, none of its
 * managed subdirs is a symlink, and its config file (if present) is a real file. Every hub reader and writer calls this at entry: the
 * no-follow checks below it are bounded BY the hub folder, so they cannot see a symlinked hub
 * folder, and the orphan prunes `readdir` a managed subdir and delete what they find — through
 * a symlinked `resources/`, a recursive delete at the link's target. `create` realizes a
 * missing hub folder as a real directory (writers); readers pass false.
 */
export function requireRealHubFolder(hubFolder: string, create: boolean): void {
  if (!create) {
    // Nothing there yet: nothing to read or write through — the caller's own "not found"
    // is the right error, not a symlink refusal.
    try {
      fs.lstatSync(hubFolder);
    } catch {
      return;
    }
  }
  requireRealSubdirNoSymlink(path.dirname(hubFolder), hubFolder, create);
  for (const sub of HUB_MANAGED_SUBDIRS) {
    requireRealSubdirNoSymlink(hubFolder, path.join(hubFolder, sub), false);
  }
  // The hub config file too, checked up front: a reader that hits it mid-way has already
  // acted (push renames agents first), and a caller that treats a parse failure as
  // "overwrite" would otherwise rewrite the folder unprompted.
  for (const name of HUB_CONFIG_FILES) {
    let st: fs.Stats | undefined;
    try {
      st = fs.lstatSync(path.join(hubFolder, name));
    } catch {
      continue;
    }
    if (!st.isFile()) throw symlinkRefusal(path.join(path.basename(hubFolder), name), 'file');
  }
}

/**
 * Read a single config file that must be a real file inside `root`, throwing (`expected`)
 * when it is a symlink or reached through one — for the read-modify-write sites that would
 * otherwise copy the link target's bytes into the workspace or write back through the link.
 * Returns null when nothing is there.
 */
export function readRealFileOrThrow(root: string, abs: string): string | null {
  let st: fs.Stats;
  try {
    st = fs.lstatSync(abs);
  } catch {
    return null;
  }
  const bytes = st.isFile() ? readFileNoFollow(root, abs) : null;
  if (!bytes) throw symlinkRefusal(path.relative(path.dirname(root), abs), 'file');
  return bytes.toString('utf-8');
}

/**
 * Read `abs` WITHOUT following a symlink at any level, returning null instead of
 * the target's bytes when the path escapes `root`, crosses a symlinked ancestor,
 * or is itself a symlink. Absent or unreadable also reads as null.
 *
 * The read-side counterpart of `writeFileNoFollow`, and it closes the same
 * threat from the other direction: where a symlink on the WRITE path overwrites
 * a file outside the workspace, one on the READ path discloses it — a committed
 * `record-types/x.yaml -> ~/.ssh/id_rsa` would otherwise be parsed as config and
 * sent upstream by the next push.
 *
 * Null is "no bytes to read", NOT "no content there": a caller for which the
 * difference matters — a desired-state config, where an empty read means DELETE
 * — must refuse rather than carry on (see `config-parser.ts`).
 */
export function readFileNoFollow(root: string, abs: string): Buffer | null {
  if (!ensureRealSubdirNoSymlink(root, path.dirname(abs), false)) return null;
  try {
    if (!fs.lstatSync(abs).isFile()) return null;
    return fs.readFileSync(abs);
  } catch {
    return null;
  }
}

/**
 * Write `data` to `abs` WITHOUT following a symlink at any level: the parent directory chain
 * is created with `ensureRealSubdirNoSymlink` (so a symlinked ancestor can never be traversed
 * — no descendant dir is created THROUGH it), and an existing symlink AT `abs` is removed so a
 * real file lands in place, never the link's target. Returns false and writes nothing if the
 * parent chain can't be realized inside `root` without crossing a symlink.
 *
 * A `false` return means "refused"; the underlying write can still THROW (EISDIR,
 * a read-only mount), so a best-effort caller needs its own `try`.
 */
export function writeFileNoFollow(root: string, abs: string, data: Buffer): boolean {
  const parent = path.dirname(abs);
  if (!ensureRealSubdirNoSymlink(root, parent, true)) return false;
  let st: fs.Stats | undefined;
  try {
    st = fs.lstatSync(abs);
  } catch {
    /* absent */
  }
  if (st?.isSymbolicLink()) fs.rmSync(abs);
  fs.writeFileSync(abs, data);
  return true;
}

/**
 * Create `abs` only when NOTHING is there yet, following no symlink at any level: the parent
 * chain is realized exactly as `writeFileNoFollow`'s is, and the exclusive create (`wx`,
 * O_CREAT|O_EXCL) refuses any existing path at `abs` — a dangling symlink included, which an
 * existence check would call absent and a plain write would follow to its target. Unlike
 * `writeFileNoFollow` it never removes what is already there.
 *
 * Returns 'created', 'exists' (something already at `abs`; untouched), or 'refused' (the
 * parent chain can't be realized inside `root` without crossing a symlink; nothing written).
 */
export function createFileNoFollow(root: string, abs: string, data: Buffer): 'created' | 'exists' | 'refused' {
  if (!ensureRealSubdirNoSymlink(root, path.dirname(abs), true)) return 'refused';
  try {
    fs.writeFileSync(abs, data, { flag: 'wx' });
  } catch (err) {
    if ((err as NodeJS.ErrnoException).code === 'EEXIST') return 'exists';
    throw err;
  }
  return 'created';
}
